import {
	ResultContainer,
	ResultContainerOuter,
	SearchDialogContent,
} from '@atlassian/search-dialog';
import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl-next';

import { LoadingSpinner } from '../../../common/loading-spinner';
import { ProductStates } from '../../product-state-machine/product-state-machine-types';
import { GenericSearchResult } from '../../search-result/search-result';
import { messages } from './messages';
import { useCurrentProductHasFilters } from '../../filters/filters-helper-functions';
import { NoPreQueryResults, NoSearchResults, PreQueryErrorScreen } from '../../no-results-screen';
import { useQuery } from '../../query-context';
import { HeightRetainingContainer } from '../../product-router/product/result-provider/height-retaining-container';
import { GetDefaultFooter } from '../../product-router/product/result-provider/result-renderer/default-footer';
import { FilterSection } from '../../product-router/product/result-provider/result-renderer/filter-section';
import { type ResultRendererPropsComplete } from '../../product-router/product/result-provider/result-renderer/result-renderer-types';
import { useIsMobileView } from '../../utils/use-is-mobile-view';
import { hasSearchResults } from '../../product-router/product/utils';
import { SearchErrorScreen } from '../../search-error-screen/search-error-screen';
import { useAutocorrection } from '../../autocorrection/autocorrection-context';
import { useAutocorrectionEnabled } from '../../autocorrection/autocorrection-helper-functions';

export const ResultRenderer: React.FC<ResultRendererPropsComplete> = ({
	productState,
	preQueryItems,
	postQueryItems,
	onRetryPreQuery,
	onRetryPostQuery,
	urlGeneratorForNoResultsScreen = () => '',
	preQuerySectionTitleGenerator,
	linkComponent,
	children,
	searchFooterLabel,
	appendQueryParams,
	isPreQueryFiltersEnabled,
}) => {
	const DefaultHeader = () => <></>;
	const DefaultSectionFooter = () => <></>;

	const intl = useIntl();
	const isMobileView = useIsMobileView();
	const areFiltersEnabled = useCurrentProductHasFilters();
	const { query } = useQuery();
	const { autocorrectionEnabled } = useAutocorrectionEnabled();
	const { correction } = useAutocorrection();

	const renderer =
		children && typeof children === 'function'
			? children({
					query,
					productState,
					linkComponent,
					preQueryItems,
					postQueryItems,
					onRetryPreQuery,
					onRetryPostQuery,
					urlGeneratorForNoResultsScreen,
					preQuerySectionTitleGenerator,
				})
			: null;

	const Header = renderer?.Header || DefaultHeader;
	const SectionFooter = renderer?.SectionFooter || DefaultSectionFooter;
	const Footer = renderer?.Footer || (
		<GetDefaultFooter
			urlGeneratorForNoResultsScreen={urlGeneratorForNoResultsScreen}
			linkComponent={linkComponent}
			productState={productState}
			searchFooterLabel={searchFooterLabel}
		/>
	);

	const renderResults = useCallback(() => {
		switch (productState) {
			case ProductStates.PostQueryError:
				return <SearchErrorScreen onRetry={onRetryPostQuery} />;
			case ProductStates.PostQueryTyping:
				return (
					<GenericSearchResult
						searchItems={postQueryItems}
						screen="cachedResults"
						forceHideResultCount={true}
						linkComponent={linkComponent}
						isCollapsed={areFiltersEnabled || isMobileView}
						productState={ProductStates.PostQueryTyping}
						appendQueryParams={appendQueryParams}
					/>
				);
			case ProductStates.PostQueryLoading:
				return (
					<GenericSearchResult
						searchItems={postQueryItems}
						generateSectionTitle={() =>
							intl.formatMessage(messages.cross_product_results_section_heading)
						}
						screen="cachedResults"
						forceHideResultCount={true}
						linkComponent={linkComponent}
						isCollapsed={areFiltersEnabled || isMobileView}
						productState={ProductStates.PostQueryLoading}
						appendQueryParams={appendQueryParams}
					>
						<LoadingSpinner />
					</GenericSearchResult>
				);
			case ProductStates.PostQueryNoResult:
				return hasSearchResults(postQueryItems) ? (
					<GenericSearchResult
						searchItems={postQueryItems}
						screen="cachedResults"
						forceHideResultCount={true}
						linkComponent={linkComponent}
						isCollapsed={areFiltersEnabled || isMobileView}
						productState={ProductStates.PostQueryLoading}
						appendQueryParams={appendQueryParams}
					/>
				) : (
					<NoSearchResults urlGeneratorForNoResultsScreen={urlGeneratorForNoResultsScreen} />
				);
			case ProductStates.PostQuerySuccess:
				return (
					<>
						<GenericSearchResult
							searchItems={postQueryItems}
							forceHideResultCount={autocorrectionEnabled && !!correction && correction.length > 0}
							linkComponent={linkComponent}
							screen="postQuerySearchResults"
							productState={ProductStates.PostQuerySuccess}
							isCollapsed={areFiltersEnabled || isMobileView}
							renderSectionViewAll={true}
							appendQueryParams={appendQueryParams}
						/>
					</>
				);
			case ProductStates.PreQueryError:
				return <PreQueryErrorScreen />;
			case ProductStates.PreQueryLoading:
				return <LoadingSpinner />;
			case ProductStates.PreQueryNoResult:
				return <NoPreQueryResults />;
			case ProductStates.PreQuerySuccess:
				return (
					<GenericSearchResult
						searchItems={preQueryItems}
						generateSectionTitle={() =>
							intl.formatMessage(messages.cross_product_recents_section_heading)
						}
						linkComponent={linkComponent}
						screen="preQuerySearchResults"
						forceHideResultCount={true}
						productState={ProductStates.PreQuerySuccess}
						isCollapsed={isMobileView}
						appendQueryParams={appendQueryParams}
					/>
				);
		}
	}, [
		correction,
		autocorrectionEnabled,
		productState,
		preQueryItems,
		postQueryItems,
		linkComponent,
		onRetryPostQuery,
		urlGeneratorForNoResultsScreen,
		areFiltersEnabled,
		isMobileView,
		appendQueryParams,
		intl,
	]);

	const body = useMemo(
		() =>
			renderer && renderer.Body && renderer?.Body() !== undefined ? renderer.Body : renderResults,
		[renderer, renderResults],
	);

	const isOnLoadingScreen =
		productState === ProductStates.PostQueryLoading ||
		productState === ProductStates.PreQueryLoading;

	const footer = typeof Footer === 'function' ? <Footer /> : Footer;

	return (
		<HeightRetainingContainer retainHeight={isOnLoadingScreen}>
			<Header />
			<SearchDialogContent>
				<ResultContainerOuter aria-live="polite">
					<ResultContainer>{body()}</ResultContainer>
					<SectionFooter />
				</ResultContainerOuter>
				{areFiltersEnabled && (
					<FilterSection
						productState={productState}
						isPreQueryFiltersEnabled={isPreQueryFiltersEnabled}
					/>
				)}
			</SearchDialogContent>
			{footer}
		</HeightRetainingContainer>
	);
};
